import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { GlobalContext } from '../context';

const Seo = ({ lang, meta }) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
          title
          description
          titleTemplate
          supportedLanguages
          author
          publisher
          image
          twitter
        }
      }
    }
  `);

  const {
    siteUrl,
    description = '',
    title = '',
    author = '',
    publisher = '',
    twitter = '',
    titleTemplate = null,
    supportedLanguages = [],
    image,
  } = site.siteMetadata || {};

  const { language } = useContext(GlobalContext) || {};
  const [hostname, setHostname] = useState(siteUrl);
  const [pathname, setPathname] = useState('');

  useEffect(() => {
    if (window.location) {
      setHostname(window.location.origin);
      setPathname(window.location.pathname);
    }
  }, [setHostname, setPathname]);

  return (
    <Helmet
      htmlAttributes={{ lang: language || lang }}
      title={title}
      titleTemplate={titleTemplate}
      link={[
        {
          rel: `preload`,
          as: `font`,
          href: `/fonts/SFProDisplay.woff2`,
          type: `font/woff2`,
          crossOrigin: `anonymous`,
        },
        {
          rel: `canonical`,
          href: `${hostname}${pathname}`,
        },
        {
          rel: `alternate`,
          hrefLang: `x-default`,
          href: hostname,
        },
        ...supportedLanguages.map((supportedLang) => ({
          rel: `alternate`,
          hrefLang: supportedLang,
          href:
            supportedLang === 'en'
              ? `${hostname}${pathname}`
              : `${hostname}/${supportedLang}${pathname}`,
        })),
      ]}
      meta={[
        {
          name: `description`,
          content: description,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: description,
        },
        {
          property: `og:locale`,
          content: language,
        },
        {
          property: `og:url`,
          content: hostname,
        },
        {
          property: `og:site_name`,
          content: title,
        },
        {
          property: `article:publisher`,
          content: publisher,
        },
        {
          property: `image`,
          content: image,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: author,
        },
        {
          name: `twitter:title`,
          content: twitter,
        },
        {
          name: `twitter:description`,
          content: description,
        },
      ].concat(meta)}
    />
  );
};

Seo.defaultProps = {
  lang: `en`,
  meta: [],
};

Seo.propTypes = {
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
};

export default Seo;
